import React, { useState, useEffect, Component } from 'react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import { AgGridReact } from 'ag-grid-react';
import { useTranslation } from 'react-i18next';
import '../pagination/pagination.css';
import Pagination from '../pagination/pagination';
import LeaseApiService from '../../services/apiService';
import { IoAddSharp } from 'react-icons/io5';
import { RiRefreshLine } from 'react-icons/ri';
import { FaFileCircleExclamation } from 'react-icons/fa6';
import Select from 'react-select';
import Modal from 'react-bootstrap/Modal';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { getName } from '../../../Redux/selecters';
import { fetchCommunityCountries, fetchCommunityCitiesByCountry, resolveCommunityRecordId } from './communityApiHelpers';
import CreateCommunity from './createCommunity';
import UpdateCommunity from './updateCommunity';

const emptyValueFormatter = (params) => {
  const v = params.value;
  if (v === null || v === undefined || v === '' || v === '0') return '--';
  return v;
};

const selectStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isFocused ? '#F5F5F5 ' : null,
    color: '#000',
    ':hover': {
      backgroundColor: '#F5F5F5',
      color: '#000 ',
    },
  }),
};

const CommunityListing = () => {
  const { t } = useTranslation();
  const apiService = new LeaseApiService();
  const userName = useSelector(getName);
  const [rowData, setRowData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageSize, setPageSize] = useState(20);
  const [pagination, setPagination] = useState();
  const [FilterTab, setFilterTab] = useState(true);
  const [contextMenu, setContextMenu] = useState(null);
  const [countriesList, setCountriesList] = useState([]);
  const [cityList, setCityList] = useState([]);
  const [formModal, setFormModal] = useState({ mode: null, data: null });
  const [formData, setFormData] = useState({
    communityNo: '',
    description: '',
    city: null,
    country: null,
  });

  useEffect(() => {
    fetchDropdownData();
    getItems();
  }, []);

  useEffect(() => {
    const handleClickOutside = () => setContextMenu(null);
    window.addEventListener('click', handleClickOutside);
    return () => window.removeEventListener('click', handleClickOutside);
  }, []);

  const fetchDropdownData = async () => {
    try {
      const countries = await fetchCommunityCountries(apiService);
      setCountriesList(countries);
    } catch (error) {
      console.error('Error fetching community dropdown data:', error);
    }
  };

  const handleCountryChange = async (selectedOption) => {
    setFormData((prev) => ({ ...prev, country: selectedOption, city: null }));
    if (!selectedOption?.label) {
      setCityList([]);
      return;
    }
    try {
      const cities = await fetchCommunityCitiesByCountry(apiService, selectedOption.label);
      setCityList(cities);
    } catch (error) {
      console.error('Error fetching cities:', error);
      setCityList([]);
    }
  };

  const getItems = async (newPageNo = pagination?.currentPage) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('PageNo', newPageNo || 1);
      params.append('PageSize', pageSize || 20);
      if (formData.communityNo) params.append('CommunityNo', formData.communityNo);
      if (formData.description) params.append('Description', formData.description);
      if (formData.city?.label) params.append('City', formData.city.label);
      if (formData.country?.label) params.append('Country', formData.country.label);
      const res = await apiService.getApiParamater('GET_COMMUNITY_LIST', `?${params.toString()}`);
      setRowData(res?.data?.result || []);
      setPagination(res?.data?.paginationData);
    } catch (error) {
      console.error('Error fetching communities:', error);
      toast.error(t('communityMaster.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handlenew = () => {
    setFormModal({ mode: 'create', data: null });
  };

  const handleEdit = (rowsData) => {
    const recordId = resolveCommunityRecordId(rowsData);
    if (!recordId) {
      toast.warning(t('communityMaster.missingRecordIdEdit'));
      return;
    }
    setFormModal({ mode: 'edit', data: rowsData });
  };

  const handleView = (rowsData) => {
    const recordId = resolveCommunityRecordId(rowsData);
    if (!recordId) {
      toast.warning(t('communityMaster.missingRecordIdView'));
      return;
    }
    setFormModal({ mode: 'view', data: rowsData });
  };

  const closeFormModal = () => {
    setFormModal({ mode: null, data: null });
  };

  const handleFormSaved = () => {
    closeFormModal();
    getItems(1);
  };

  const handleDelete = async (rowsData) => {
    const recordId = resolveCommunityRecordId(rowsData);
    const communityNo = rowsData?.communityNo ?? rowsData?.CommunityNo ?? '';
    if (!window.confirm(`Delete community ${communityNo || recordId}?`)) return;
    try {
      const body = {
        recordId: String(recordId || '0'),
        communityNo: String(communityNo || '0'),
        modifiedBy: userName || '',
      };
      const res = await apiService.delete('DELETE_COMMUNITY', body);
      if (res?.data?.isSuccessful || res?.data?.statusCode === 200 || res?.data?.statusCode === 201) {
        toast.success(res?.data?.message || t('communityMaster.deleteSuccess'));
        getItems();
      } else {
        toast.warning(res?.data?.message || t('communityMaster.deleteFailed'));
      }
    } catch (error) {
      console.error('Error deleting community:', error);
      toast.error(t('communityMaster.deleteFailed'));
    }
  };

  const handleMenuAction = (action, data) => {
    if (action === 'edit') handleEdit(data);
    if (action === 'view') handleView(data);
    if (action === 'delete') handleDelete(data);
    setContextMenu(null);
  };

  const handleClearFilters = () => {
    setFormData({ communityNo: '', description: '', city: null, country: null });
    setCityList([]);
    getItems(1);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDropDownChange = ({ name, value }) => {
    if (name === 'city') {
      setFormData((prev) => ({ ...prev, city: value }));
      return;
    }
    handleCountryChange(value);
  };

  const columnDefs = [
    {
      headerName: '',
      field: 'record_Id',
      cellRenderer: BtnCellRenderer,
      pinned: 'left',
      width: 50,
      suppressSizeToFit: true,
      valueGetter: (p) => resolveCommunityRecordId(p.data),
    },
    {
      headerName: t('communityMaster.columns.communityNo'),
      field: 'communityNo',
      filter: true,
      floatingFilter: true,
      suppressMovable: true,
      flex: 1,
      minWidth: 140,
      valueGetter: (p) => p.data?.communityNo ?? p.data?.CommunityNo ?? '',
      valueFormatter: emptyValueFormatter,
    },
    {
      headerName: t('communityMaster.columns.description'),
      field: 'description',
      filter: true,
      floatingFilter: true,
      suppressMovable: true,
      flex: 2,
      minWidth: 200,
      valueFormatter: emptyValueFormatter,
    },
    {
      headerName: t('communityMaster.columns.city'),
      field: 'city',
      filter: true,
      floatingFilter: true,
      suppressMovable: true,
      flex: 1,
      minWidth: 140,
      valueFormatter: emptyValueFormatter,
    },
    {
      headerName: t('communityMaster.columns.country'),
      field: 'country',
      filter: true,
      floatingFilter: true,
      suppressMovable: true,
      flex: 1,
      minWidth: 140,
      valueFormatter: emptyValueFormatter,
    },
  ];

  const onGridReady = (params) => {
    params.api.sizeColumnsToFit();
  };

  return (
    <div className="p-3">
      <div className="cost-center-section">
        <div className="d-flex mt-3 cursor-pointer slide-header" onClick={() => setFilterTab(!FilterTab)}>
          <div className="h6 pt-1">{t('common.searchParameters')}</div>
        </div>
        <div className={`slide-content ${FilterTab ? 'open' : 'closed'}`}>
          <div className={`border-box2 p-3 ${FilterTab ? 'slide-in' : 'slide-out'}`}>
            <div className="form-group grid-contain ">
              <div className="d-flex">
                <label className="form-label pt-2 start">{t('communityMaster.filters.communityNo')}</label>
                <input type="text" className="p-2 form-control w-75" name="communityNo" value={formData.communityNo} onChange={handleInputChange} />
              </div>
              <div className="d-flex">
                <label className="form-label pt-2 start">{t('communityMaster.filters.description')}</label>
                <input type="text" className="p-2 form-control w-75" name="description" value={formData.description} onChange={handleInputChange} />
              </div>
              <div className="d-flex">
                <label className="form-label pt-2 start">{t('communityMaster.filters.country')}</label>
                <Select
                  className="p-0 w-100"
                  menuPortalTarget={document.body}
                  styles={selectStyles}
                  theme={(theme) => ({ ...theme, borderRadius: 5, padding: 9 })}
                  value={formData.country}
                  onChange={(selectedOption) => handleCountryChange(selectedOption)}
                  options={countriesList?.map((item) => ({ value: item, label: item?.text }))}
                  isClearable
                />
              </div>
              <div className="d-flex">
                <label className="form-label pt-2 start">{t('communityMaster.filters.city')}</label>
                <Select
                  className="p-0 w-100"
                  menuPortalTarget={document.body}
                  styles={selectStyles}
                  theme={(theme) => ({ ...theme, borderRadius: 5, padding: 9 })}
                  value={formData.city}
                  onChange={(selectedOption) => handleDropDownChange({ name: 'city', value: selectedOption })}
                  options={cityList?.map((item) => ({ value: item, label: item?.text }))}
                  isDisabled={!formData.country}
                  isClearable
                />
              </div>
              <div className="d-flex gap-1">
                <div onClick={() => getItems()} className="mainbtnGry flex-end gap-2 w-50">
                  <FaFileCircleExclamation color="#fff" className="center mt-2 mx-2" />
                  <div className="center">{t('common.search')}</div>
                </div>
                <div onClick={handleClearFilters} className="mainbtnRed flex-end gap-2 w-50">
                  <RiRefreshLine color="#fff" className="center mt-2 mx-2" />
                  <div className="center">{t('common.reset')}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="border-box2 p-3">
        {loading ? (
          <div>{t('common.loading')}</div>
        ) : (
          <>
            <div className="d-flex justify-contant-between ">
              <h5 className="col mainInvHead">{t('communityMaster.pageTitle')}</h5>
              <div className="d-flex justify-content-end gap-1">
                <div className="col center">
                  <button className="mainbtn" type="button" onClick={handlenew}>
                    <IoAddSharp color="#fff" className="center mt-1" />
                    <div>{t('common.add')}</div>
                  </button>
                </div>
              </div>
            </div>
            <div className="gridshad mt-2" style={{ width: '100%' }}>
              <div className="ag-theme-quartz" style={{ height: 650, width: '100%' }}>
                <AgGridReact
                  getRowStyle={(params) => ({
                    background: params.node.rowIndex % 2 === 0 ? '#F0EDF5' : '#fff',
                  })}
                  rowData={rowData}
                  enableFilter={true}
                  floatingFilter={true}
                  columnDefs={columnDefs}
                  onGridReady={onGridReady}
                  onFirstDataRendered={onGridReady}
                  frameworkComponents={{ BtnCellRenderer }}
                  context={{ setContextMenu }}
                />
              </div>
              <Pagination pageSize={pageSize} setPageSize={setPageSize} pagination={pagination} getItems={getItems} />
            </div>
          </>
        )}
        {contextMenu && (
          <div
            style={{
              position: 'absolute',
              top: contextMenu.mouseY,
              left: contextMenu.mouseX,
              backgroundColor: 'white',
              border: '1px solid gray',
              zIndex: 1000,
              padding: '10px 10px',
              borderRadius: '8px',
              boxShadow: '0px 2px 6px rgba(0,0,0,0.2)',
            }}
          >
            <div className="d-flex flex-column gap-2">
              <button className="btn btn-secondary" onClick={() => handleMenuAction('view', contextMenu.data)}>
                {t('common.view')}
              </button>
              <button className="btn btn-secondary" onClick={() => handleMenuAction('edit', contextMenu.data)}>
                {t('common.edit')}
              </button>
              <button className="btn btn-danger" onClick={() => handleMenuAction('delete', contextMenu.data)}>
                {t('common.delete')}
              </button>
            </div>
          </div>
        )}
      </div>

      <Modal
        show={!!formModal.mode}
        onHide={closeFormModal}
        backdrop="static"
        keyboard={false}
        centered
        dialogClassName="community-form-modal"
        contentClassName="p-0"
      >
        <Modal.Body style={{ padding: 0 }}>
          {formModal.mode === 'create' && <CreateCommunity onClose={closeFormModal} onSaved={handleFormSaved} />}
          {(formModal.mode === 'edit' || formModal.mode === 'view') && (
            <UpdateCommunity
              initialData={formModal.data}
              viewOnly={formModal.mode === 'view'}
              onClose={closeFormModal}
              onSaved={handleFormSaved}
            />
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

class BtnCellRenderer extends Component {
  constructor(props) {
    super(props);
    this.handleClick = this.handleClick.bind(this);
  }

  handleClick(e) {
    e.stopPropagation();
    const { context, data } = this.props;
    if (context?.setContextMenu) {
      context.setContextMenu({
        mouseX: e.clientX,
        mouseY: e.clientY,
        data,
      });
    }
  }

  render() {
    return (
      <div className="center pt-1">
        <span onClick={this.handleClick} style={{ cursor: 'pointer', fontSize: '25px' }}>
          &#8942;
        </span>
      </div>
    );
  }
}

export default CommunityListing;
